/**
 * 轻量关系型存档数据库（localStorage 版）。
 *
 * 设计目标：
 *  1. 表 + 主键 + 索引 + 类 SQL 的查询接口，后续系统只面对 DB，不直接碰 localStorage；
 *  2. 所有写操作自动标记脏，由引擎节流落盘；
 *  3. 带版本号与迁移钩子，存档结构升级不炸档；
 *  4. 可整体导出 / 导入 JSON，方便备份与联机存档同步。
 *
 * 之后若要换成 IndexedDB / 远端服务，只需替换 Storage 适配器。
 */

/** 存储适配器：默认 localStorage，内存兜底 */
export class StorageAdapter {
  constructor(key = 'dongtianju:save', useMemory = false) {
    this.key = key;
    this.memory = null;
    this.useMemory = useMemory || !StorageAdapter.isAvailable();
  }

  static isAvailable() {
    try {
      const t = '__dt_test__';
      localStorage.setItem(t, '1');
      localStorage.removeItem(t);
      return true;
    } catch {
      return false;
    }
  }

  read() {
    if (this.useMemory) return this.memory;
    try {
      const s = localStorage.getItem(this.key);
      return s ? JSON.parse(s) : null;
    } catch (e) {
      console.warn('[DB] 存档读取失败，转为内存模式', e);
      this.useMemory = true;
      return null;
    }
  }

  write(obj) {
    const s = JSON.stringify(obj);
    if (this.useMemory) { this.memory = s; return true; }
    try {
      localStorage.setItem(this.key, s);
      return true;
    } catch (e) {
      console.warn('[DB] 存档写入失败', e);
      this.useMemory = true;
      this.memory = s;
      return false;
    }
  }

  clear() {
    this.memory = null;
    if (!this.useMemory) localStorage.removeItem(this.key);
  }
}

/** 单表 */
export class Table {
  constructor(db, name, def = {}) {
    this.db = db;
    this.name = name;
    this.pk = def.pk || 'id';
    this.autoInc = def.autoInc !== false;
    this.indexes = def.indexes || [];   // 常用查询字段，建 Map 索引
    this.defaults = def.defaults || {};
    this._rows = [];
    this._index = new Map();
    this._seq = 1;
  }

  _reindex() {
    this._index.clear();
    for (const f of this.indexes) this._index.set(f, new Map());
    for (const row of this._rows) {
      for (const f of this.indexes) {
        const m = this._index.get(f);
        const k = row[f];
        if (!m.has(k)) m.set(k, new Set());
        m.get(k).add(row);
      }
    }
  }

  all() { return this._rows.slice(); }
  count() { return this._rows.length; }

  find(pkValue) {
    return this._rows.find((r) => r[this.pk] === pkValue) || null;
  }

  /** 条件查询：fn(row) => boolean */
  where(fn) { return this._rows.filter(fn); }
  first(fn) { return this._rows.find(fn) || null; }

  /** 走索引的等值查询，未建索引时退化为全表扫描 */
  whereEq(field, value) {
    const m = this._index.get(field);
    if (m) return Array.from(m.get(value) || []);
    return this.where((r) => r[field] === value);
  }

  insert(row) {
    const r = { ...this.defaults, ...row };
    if (this.autoInc && r[this.pk] === undefined) r[this.pk] = this._seq++;
    else if (typeof r[this.pk] === 'number' && r[this.pk] >= this._seq) this._seq = r[this.pk] + 1;
    this._rows.push(r);
    for (const f of this.indexes) {
      if (!this._index.has(f)) this._index.set(f, new Map());
      const m = this._index.get(f);
      if (!m.has(r[f])) m.set(r[f], new Set());
      m.get(r[f]).add(r);
    }
    this.db._touch();
    return r;
  }

  update(pkValue, patch) {
    const row = this.find(pkValue);
    if (!row) return null;
    for (const f of this.indexes) {
      const m = this._index.get(f);
      if (m && m.has(row[f])) m.get(row[f]).delete(row);
    }
    Object.assign(row, patch);
    for (const f of this.indexes) {
      const m = this._index.get(f);
      if (!m.has(row[f])) m.set(row[f], new Set());
      m.get(row[f]).add(row);
    }
    this.db._touch();
    return row;
  }

  /** 有则改、无则插（按 pk 或指定唯一键） */
  upsert(row, keyField) {
    const kf = keyField || this.pk;
    const exist = kf === this.pk ? this.find(row[this.pk]) : this.first((r) => r[kf] === row[kf]);
    if (exist) return this.update(exist[this.pk], row);
    return this.insert(row);
  }

  remove(pkValue) {
    const i = this._rows.findIndex((r) => r[this.pk] === pkValue);
    if (i < 0) return false;
    const [row] = this._rows.splice(i, 1);
    for (const f of this.indexes) {
      const m = this._index.get(f);
      if (m && m.has(row[f])) m.get(row[f]).delete(row);
    }
    this.db._touch();
    return true;
  }

  clear() {
    this._rows.length = 0;
    this._seq = 1;
    this._reindex();
    this.db._touch();
  }

  toJSON() { return { seq: this._seq, rows: this._rows }; }

  fromJSON(obj) {
    this._rows = (obj && obj.rows ? obj.rows : []).map((r) => ({ ...this.defaults, ...r }));
    this._seq = (obj && obj.seq) || this._rows.length + 1;
    this._reindex();
  }
}

export class Database {
  constructor({ version = 1, storageKey = 'dongtianju:save', migrations = [] } = {}) {
    this.version = version;
    this.storage = new StorageAdapter(storageKey);
    this.migrations = migrations;
    this.tables = new Map();
    this.dirty = false;
    this.autoSaveInterval = 20;   // 秒
    this._autoAcc = 0;
    this.lastSavedAt = 0;
    this.onSave = null;
    this.onLoad = null;
  }

  define(name, def) {
    const t = new Table(this, name, def);
    this.tables.set(name, t);
    return t;
  }

  table(name) {
    const t = this.tables.get(name);
    if (!t) throw new Error(`[DB] 未定义的表: ${name}`);
    return t;
  }

  _touch() { this.dirty = true; }

  /** 新档：清空并按 seed 回调填充初始数据 */
  newGame(seedFn) {
    for (const t of this.tables.values()) t.clear();
    if (seedFn) seedFn(this);
    this.dirty = true;
    this.save(true);
    return this;
  }

  hasSave() { return !!this.storage.read(); }

  save(force = false) {
    if (!this.dirty && !force) return false;
    const obj = { version: this.version, savedAt: Date.now(), tables: {} };
    for (const [name, t] of this.tables) obj.tables[name] = t.toJSON();
    const ok = this.storage.write(obj);
    this.dirty = false;
    this.lastSavedAt = Date.now();
    if (this.onSave) this.onSave(obj);
    return ok;
  }

  load() {
    const obj = this.storage.read();
    if (!obj) return false;
    if (obj.version !== this.version) this._migrate(obj);
    for (const [name, t] of this.tables) {
      if (obj.tables && obj.tables[name]) t.fromJSON(obj.tables[name]);
      else t.clear();
    }
    this.dirty = false;
    if (this.onLoad) this.onLoad(obj);
    return true;
  }

  _migrate(obj) {
    const list = this.migrations
      .filter((m) => m.from === obj.version)
      .sort((a, b) => a.from - b.from);
    for (const m of list) {
      try { m.up(obj); obj.version = m.to; }
      catch (e) { console.error('[DB] 迁移失败', m, e); }
    }
    obj.version = this.version;
  }

  /** 引擎每帧调用，做节流自动保存 */
  tick(dt) {
    this._autoAcc += dt;
    if (this._autoAcc >= this.autoSaveInterval) {
      this._autoAcc = 0;
      if (this.dirty) this.save();
    }
  }

  export() {
    const obj = { version: this.version, savedAt: Date.now(), tables: {} };
    for (const [name, t] of this.tables) obj.tables[name] = t.toJSON();
    return JSON.stringify(obj, null, 2);
  }

  import(json) {
    const obj = typeof json === 'string' ? JSON.parse(json) : json;
    for (const [name, t] of this.tables) {
      if (obj.tables && obj.tables[name]) t.fromJSON(obj.tables[name]);
    }
    this.dirty = true;
    this.save(true);
    return true;
  }

  reset() {
    this.storage.clear();
    for (const t of this.tables.values()) t.clear();
    this.dirty = false;
  }
}
